/**
 * BookingsBoard — single component used on Customer / Vendor / Professional
 * dashboards. Reads /api/bookings/mine and auto-adapts its action set
 * depending on the viewer's role (passed in as a prop because the
 * AuthContext shape varies slightly across surfaces).
 *
 * Customer view:
 *   - Phone column shows vendor_phone (visible once provider accepts)
 *   - "Show event-day OTP" button when status ∈ {accepted, in_progress}
 *   - "Leave review" form when status = completed
 *
 * Provider view (vendor / professional):
 *   - Phone column shows customer_phone (visible once they accept)
 *   - Accept / Reject buttons when status = pending
 *   - Mark complete (OTP entry) when status ∈ {accepted, in_progress}
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Star, Check, X as XIcon, Hourglass, Receipt } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_PILL = {
  pending:     'bg-amber-100 text-amber-800',
  accepted:    'bg-blue-100 text-blue-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  completed:   'bg-green-100 text-green-800',
  rejected:    'bg-red-100 text-red-700',
  cancelled:   'bg-gray-100 text-gray-700',
};
const PROVIDER_ROLES = new Set(['vendor', 'professional']);

const StatusBadge = ({ status }) => (
  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${STATUS_PILL[status] || STATUS_PILL.pending}`}
    data-testid={`booking-status-${status}`}>
    {status || 'pending'}
  </span>
);

const StarRow = ({ value, onChange, readOnly, testId }) => (
  <div className="inline-flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        type="button"
        key={n}
        disabled={readOnly}
        onClick={readOnly ? undefined : () => onChange(n)}
        className={`${readOnly ? 'cursor-default' : 'cursor-pointer'} text-xl ${n <= value ? 'text-amber-500' : 'text-gray-300'}`}
        data-testid={testId ? `${testId}-star-${n}` : undefined}
        aria-label={`${n} star${n > 1 ? 's' : ''}`}
      >
        <Star size={20} weight={n <= value ? 'fill' : 'regular'} />
      </button>
    ))}
  </div>
);

const BookingsBoard = ({ viewerRole, title = 'My Bookings', testId = 'bookings-board' }) => {
  const isProvider = PROVIDER_ROLES.has(viewerRole);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [otpFor, setOtpFor] = useState({}); // bookingId -> otp string (customer)
  const [otpInput, setOtpInput] = useState({}); // bookingId -> input (provider)
  const [reviewDraft, setReviewDraft] = useState({}); // bookingId -> {stars, text}

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/bookings/mine`, { withCredentials: true });
      setBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to load bookings.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const callAction = async (bookingId, path, body) => {
    try {
      const url = `${API}/bookings/${bookingId}/${path}`;
      const res = body ? await axios.post(url, body, { withCredentials: true })
                       : await axios.post(url, null, { withCredentials: true });
      toast.success({ accept: 'Order accepted', reject: 'Order rejected',
        'mark-in-progress': 'Marked in progress', 'mark-complete': 'Order completed!' }[path] || 'Done');
      // Optimistically replace the row.
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? res.data : b)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    }
  };

  const fetchOtp = async (bookingId) => {
    try {
      const { data } = await axios.get(`${API}/bookings/${bookingId}/event-otp`, { withCredentials: true });
      setOtpFor((prev) => ({ ...prev, [bookingId]: data.otp }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'OTP not ready yet.');
    }
  };

  const submitReview = async (bookingId) => {
    const draft = reviewDraft[bookingId] || {};
    if (!draft.stars) { toast.error('Pick at least 1 star'); return; }
    try {
      await axios.post(`${API}/bookings/${bookingId}/review`,
        { stars: draft.stars, text: draft.text || '' },
        { withCredentials: true });
      toast.success('Review posted. Thank you!');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed.');
    }
  };

  if (loading) return (
    <Card className="bg-white border-[#E8E1D5]" data-testid={`${testId}-loading`}>
      <CardContent className="p-6 text-sm text-text-secondary">Loading bookings…</CardContent>
    </Card>
  );

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid={testId}>
      <CardHeader>
        <CardTitle className="font-heading text-2xl flex items-center gap-2"><Receipt size={22} /> {title}</CardTitle>
        <CardDescription className="text-sm">
          {isProvider
            ? 'Accept or reject incoming bookings, then complete with the customer\'s event-day OTP.'
            : 'Track your booked vendors & pros. Show the event-day OTP to your provider on the day to mark the work complete.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {bookings.length === 0 && (
          <div className="text-center py-8 text-sm text-text-secondary border border-dashed border-[#E8E1D5] rounded" data-testid={`${testId}-empty`}>
            No bookings yet.
          </div>
        )}
        {bookings.map((b) => {
          const otherName = isProvider ? b.customer_name : b.vendor_name;
          const otherPhone = isProvider ? b.customer_phone : b.vendor_phone;
          const otpAvailable = !isProvider && ['accepted', 'in_progress'].includes(b.status);
          const showAcceptReject = isProvider && b.status === 'pending';
          const showCompleteForm = isProvider && ['accepted', 'in_progress'].includes(b.status);
          const showReviewForm = !isProvider && b.status === 'completed' && !b.review_id;
          const reviewed = !!b.review_id;
          return (
            <div key={b.id} className="border border-[#E8E1D5] rounded p-3" data-testid={`booking-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StatusBadge status={b.status} />
                    {reviewed && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Reviewed</span>}
                  </div>
                  <div className="font-bold text-sm">{b.service_name || b.event_type || 'Booking'}</div>
                  <div className="text-[11px] text-text-secondary mt-0.5">
                    {isProvider ? <>From <strong>{otherName || 'Customer'}</strong></> : <>With <strong>{otherName || 'Provider'}</strong></>}
                    {b.event_date && <> · Event: <strong>{b.event_date}</strong></>}
                  </div>
                  {otherPhone ? (
                    <a href={`tel:${otherPhone}`} className="inline-flex items-center gap-1 mt-1 text-xs text-brand-primary font-bold hover:underline" data-testid={`booking-phone-${b.id}`}>
                      <Phone size={12} /> {otherPhone}
                    </a>
                  ) : !isProvider && b.status === 'pending' ? (
                    <div className="text-[11px] text-text-secondary mt-1 italic flex items-center gap-1"><Hourglass size={11} /> Phone unlocks once provider accepts</div>
                  ) : null}
                </div>
                <div className="text-right">
                  <div className="font-heading text-lg font-bold text-brand-primary">₹{Number(b.total_price || 0).toLocaleString('en-IN')}</div>
                </div>
              </div>

              {/* Provider actions */}
              {showAcceptReject && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#E8E1D5]">
                  <Button type="button" size="sm" className="rounded-none bg-green-600 hover:bg-green-700 text-white" onClick={() => callAction(b.id, 'accept')} data-testid={`booking-accept-${b.id}`}>
                    <Check size={14} className="mr-1" /> Accept
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" onClick={() => callAction(b.id, 'reject')} data-testid={`booking-reject-${b.id}`}>
                    <XIcon size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              )}
              {showCompleteForm && (
                <div className="mt-3 pt-3 border-t border-[#E8E1D5] flex flex-wrap items-center gap-2" data-testid={`booking-complete-form-${b.id}`}>
                  {b.status === 'accepted' && (
                    <Button type="button" size="sm" variant="outline" className="rounded-none border-[#E8E1D5]" onClick={() => callAction(b.id, 'mark-in-progress')} data-testid={`booking-in-progress-${b.id}`}>
                      Mark In Progress
                    </Button>
                  )}
                  <Input type="text" maxLength={6} placeholder="6-digit OTP" inputMode="numeric"
                    value={otpInput[b.id] || ''}
                    onChange={(e) => setOtpInput((p) => ({ ...p, [b.id]: e.target.value.replace(/\D/g, '') }))}
                    className="w-32 h-9 rounded-none border-[#E8E1D5]"
                    data-testid={`booking-otp-input-${b.id}`} />
                  <Button type="button" size="sm" className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-white"
                    onClick={() => callAction(b.id, 'mark-complete', { otp: otpInput[b.id] })}
                    disabled={(otpInput[b.id] || '').length !== 6}
                    data-testid={`booking-mark-complete-${b.id}`}>
                    Verify & Complete
                  </Button>
                </div>
              )}

              {/* Customer actions */}
              {otpAvailable && (
                <div className="mt-3 pt-3 border-t border-[#E8E1D5] flex flex-wrap items-center gap-2">
                  {otpFor[b.id] ? (
                    <div className="font-mono text-2xl tracking-[0.4em] font-bold text-brand-primary bg-amber-50 border border-amber-300 px-3 py-1.5 rounded" data-testid={`booking-otp-display-${b.id}`}>
                      {otpFor[b.id]}
                    </div>
                  ) : (
                    <Button type="button" size="sm" variant="outline" className="rounded-none border-amber-400 text-amber-700 hover:bg-amber-50"
                      onClick={() => fetchOtp(b.id)}
                      data-testid={`booking-show-otp-${b.id}`}>
                      Show event-day OTP
                    </Button>
                  )}
                  <span className="text-[11px] text-text-secondary">Share this OTP with your provider on the event day.</span>
                </div>
              )}
              {showReviewForm && (
                <div className="mt-3 pt-3 border-t border-[#E8E1D5] space-y-2" data-testid={`booking-review-form-${b.id}`}>
                  <div className="text-xs uppercase tracking-wider font-bold text-text-secondary">Rate your experience</div>
                  <StarRow value={(reviewDraft[b.id] || {}).stars || 0}
                    onChange={(s) => setReviewDraft((p) => ({ ...p, [b.id]: { ...(p[b.id] || {}), stars: s } }))}
                    testId={`booking-review-stars-${b.id}`} />
                  <Textarea rows={2} placeholder="Optional — what went well?" value={(reviewDraft[b.id] || {}).text || ''}
                    onChange={(e) => setReviewDraft((p) => ({ ...p, [b.id]: { ...(p[b.id] || {}), text: e.target.value } }))}
                    className="rounded-none border-[#E8E1D5]"
                    data-testid={`booking-review-text-${b.id}`} />
                  <Button type="button" size="sm" className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-white"
                    onClick={() => submitReview(b.id)}
                    data-testid={`booking-review-submit-${b.id}`}>
                    Post review
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default BookingsBoard;
